<template>
    <Row class="DocumentSubmissionHistoryTableRow" hoverable>
        <Cell
            data-test-id="DocumentSubmissionHistoryTableRow__requestDate"
        >
            {{ requestDate }}
        </Cell>
        <Cell data-test-id="DocumentSubmissionHistoryTableRow__submissionDateCell">
            <div class="flex justify-start item-center">
                <EidIcon
                    v-if="submission.isNoResponse || !submission.submissionDate || declineReason"
                    v-tooltip="tooltipMessage"
                    class="text-eidDangerRed mr-1.5"
                    data-test-id="DocumentSubmissionHistoryTableRow__submissionErrorIcon"
                    :icon-src="faExclamationTriangle"
                />
                <span data-test-id="DocumentSubmissionHistoryTableRow__submissionDateText">
                    {{ declineReason || submissionDate }}
                </span>
            </div>
        </Cell>
        <Cell data-test-id="DocumentSubmissionHistoryTableRow__requirementTypeCell">
            <div class="flex justify-start item-center">
                <EidIcon
                    v-if="requirementTypeIconDetails"
                    v-tooltip="requirementTypeIconDetails.tooltip"
                    :class="requirementTypeIconDetails.class"
                    :data-test-id="requirementTypeIconDetails.dataTestId"
                    :icon-src="requirementTypeIconDetails.icon"
                />
                <span data-test-id="DocumentSubmissionHistoryTableRow__coverageTypeText">
                    {{ requirementTypeLabel }}
                </span>
            </div>
        </Cell>
        <Cell
            v-tooltip="{ popperClass: 'VTooltip--dark', content: viewDocumentTooltipMsgOrBlank }"
            data-test-id="DocumentSubmissionHistoryTableRow__viewDocumentCell"
            :class="{'cursor-not-allowed opacity-30': shouldDisableDocument}"
        >
            <DocumentDataOnlyBadge v-if="isShowingDataOnlyBadge" />
            <div
                v-else-if="submission.isNoResponse"
                class="text-sm inline-flex"
            >
                <EidIcon
                    v-tooltip="'Entity did not respond to this request.'"
                    class="text-eidDangerRed"
                    data-test-id="DocumentSubmissionHistoryTableRow__viewDocumentErrorIcon"
                    :icon-src="faExclamationTriangle"
                />
                <span class="inline-block ml-1.5">
                    -
                </span>
            </div>
            <DocumentViewButton
                v-else-if="submissionHasResponse"
                :disable="shouldDisableDocument"
                @click="showDocument"
            />
        </Cell>
    </Row>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import moment from 'moment';

    import { faExclamationTriangle, faStar, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';
    import { TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import { SubmissionMethod } from '@evidentid/tprm-portal-lib/models/common/SubmissionMethod.model';

    import { HistoricDocumentSubmission } from '@/models/HistoricDocumentSubmission.model';
    import { DashboardState, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import DocumentDataOnlyBadge
        from '@/modules/entity-details/components/DocumentDataOnlyBadge/DocumentDataOnlyBadge.vue';
    import DocumentViewButton from '@/modules/entity-details/components/DocumentViewButton/DocumentViewButton.vue';
    import {
        isFieldImportApiOrWithoutDocumentUpload,
    } from '@/modules/entity-details/utils/isFieldImportApiOrWithoutDocumentUpload/isFieldImportApiOrWithoutDocumentUpload';

    import { DECLINE_REASON_TEXTS, DECLINE_REASON_TOOLTIP_TEXTS } from './config';

    export default defineComponent({
        name: 'DocumentSubmissionHistoryTableRow',
        components: {
            DocumentViewButton,
            DocumentDataOnlyBadge,
            Cell,
            EidIcon,
            Row,
        },
        props: {
            submission: {
                type: Object as PropType<HistoricDocumentSubmission>,
                required: true,
            },
        },
        emits: [ 'show-document' ],
        data() {
            return {
                faExclamationTriangle,
                faStar,
                faHourglassHalf,
                SubmissionMethod,
            };
        },
        computed: {
            requirementDefinitions(): DashboardState['tprmRequirementsDefinitions'] {
                return (useDashboardStore().$state as DashboardState).tprmRequirementsDefinitions;
            },
            requirementDefinition(): TprmRequirementDefinition | null {
                return this.requirementDefinitions[this.submission.coverageType];
            },
            submissionHasResponse(): boolean {
                return !this.submission.isNoResponse;
            },
            shouldShowDocument(): boolean {
                return this.submissionHasResponse;
            },
            isShowingDataOnlyBadge(): boolean {
                return isFieldImportApiOrWithoutDocumentUpload({
                    submissionMethod: this.submission.submissionMethod,
                    requirementDefinition: this.requirementDefinition,
                });
            },
            requestDate(): string {
                return this.submission.requestDate?.split('T')[0] || '-';
            },
            submissionDate(): string {
                const date = this.submission.submissionDate?.split('T')[0] || '-';
                return !this.submission.isNoResponse && date || '-';
            },
            requirementTypeIconDetails(): { icon: any, class: string, tooltip: string, dataTestId: string } | null {
                const classNames = 'mr-1.5';
                if (this.submission.isCurrentSubmission) {
                    return {
                        icon: faStar,
                        class: `${classNames} text-eidTrustGreen`,
                        tooltip: 'Current document used for compliance.',
                        dataTestId: 'DocumentSubmissionHistoryTableRow__currentIcon',
                    };
                }
                if (this.statusIcon) {
                    return {
                        icon: this.statusIcon,
                        class: `${classNames} ${this.statusIconClass}`,
                        tooltip: this.statusTooltip || '',
                        dataTestId: 'DocumentSubmissionHistoryTableRow__statusIcon',
                    };
                }
                if (this.submission.isNotProvided) {
                    return {
                        icon: faExclamationTriangle,
                        class: `${classNames} text-eidDangerRed`,
                        tooltip: 'Requirement was not provided in submission.',
                        dataTestId: 'DocumentSubmissionHistoryTableRow__coverageTypeErrorIcon',
                    };
                }
                return null;
            },
            statusIcon(): object | null {
                const status = this.submission.verificationRequestStatus;
                if ([ 'PROCESSING', 'ACTIONS_REVIEW', 'AUDIT' ].includes(status)) {
                    return faHourglassHalf;
                }
                if ([ 'MEMBER_OUTREACH', 'PRODUCER_OUTREACH' ].includes(status)) {
                    return faStar;
                }
                return null;
            },
            statusTooltip(): string | null {
                const status = this.submission.verificationRequestStatus;
                switch (status) {
                    case 'PROCESSING':
                        return 'Requirement is currently being processed.';
                    case 'ACTIONS_REVIEW':
                        return 'Requirement has one or more actions for you to review.';
                    case 'AUDIT':
                        return 'Requirement is current in audit for review.';
                    case 'MEMBER_OUTREACH':
                        return 'Entity is being engaged to remedy lack of response.';
                    case 'PRODUCER_OUTREACH':
                        return 'Entity\'s producer is being engaged to remedy repeated non-compliance.';
                    default:
                        return null;
                }
            },
            statusIconClass(): string {
                const status = this.submission.verificationRequestStatus;
                if ([ 'PROCESSING', 'ACTIONS_REVIEW' ].includes(status)) {
                    return '';
                }
                if ([ 'AUDIT', 'MEMBER_OUTREACH', 'PRODUCER_OUTREACH' ].includes(status)) {
                    return 'text-eidTrustGreen';
                }
                return '';
            },
            shouldDisableDocument(): boolean {
                if (
                    !this.submission.submissionDate
                    || this.submission.isNoResponse
                    || (
                        !this.submission.isNoResponse
                        && this.submission.submissionMethod === SubmissionMethod.FieldImportApi
                    )
                ) {
                    return false;
                }
                const submissionMoment = moment(new Date(this.submission.submissionDate));
                const supportingDateMoment = moment(new Date('2022-06-01'));
                return submissionMoment.isBefore(supportingDateMoment);
            },
            viewDocumentTooltipMsgOrBlank(): string | null {
                return this.shouldDisableDocument ? 'Document not viewable. Contact support@evidentid.com to access.' : null;
            },
            declineReason(): string | null {
                if (!this.submission.declineReason) {
                    return null;
                }
                return DECLINE_REASON_TEXTS[this.submission.declineReason];
            },
            tooltipMessage(): string {
                const noSubmissionMsg = 'Entity did not respond to this request.';
                if (!this.submission.declineReason) {
                    return noSubmissionMsg;
                }
                return this.declineReason
                    ? DECLINE_REASON_TOOLTIP_TEXTS[this.submission.declineReason]
                    : noSubmissionMsg;
            },
            requirementTypeLabel(): string {
                return this.submission.requirementInstanceLabel
                    ? `${this.submission.coverageTypeLabel} - ${this.submission.requirementInstanceLabel}`
                    : this.submission.coverageTypeLabel;
            },
        },
        methods: {
            showDocument(): void {
                if (this.shouldShowDocument) {
                    const customNoDocMsg = `The documents for ${this.requirementTypeLabel} are being processed and are not yet available. Please check back shortly.`;
                    const payload = {
                        submission: this.submission,
                        ...(this.submission.verificationRequestStatus === 'PROCESSING'
                            ? { customNoDocMsg }
                            : {}
                        ),
                    };
                    this.$emit('show-document', payload);
                }
            },
        },
    });
</script>
